/** Extra stack flags owned by the battle kernel.
 *
 * `state.ts` owns bits 0..16 of `flags` and, for the content-derived war-machine
 * roles, 24..26; bits 17..23 and 27..28 are minted here so the two files never
 * collide. All of the bits in this file are bookkeeping, never content-derived.
 *
 * Why there are two words
 * -----------------------
 * `flags` is full. Bits 0..30 are spoken for and 31 is the sign bit of the
 * Int32 the column is stored in, so the next bit had nowhere to go - and three
 * bits had already been minted on top of bits that were taken. Listed rather
 * than argued (`node` over every `1 << n` in both files, 2026-09-01):
 *
 *     19  F_RETAL_MASK low bit  +  F_PREEMPTED
 *     20  F_RETAL_MASK high bit +  F_MEDITATING
 *     30  F_SCHOOL              +  F_MEDITATED
 *
 * Every one of those is a live rule bug, not a tidiness complaint. A stack that
 * had retaliated once read as having already fired its preemptive shot; a
 * Sun Serpent that meditated set the high bit of its own retaliation counter and
 * read as having spent two; and every Blackwater School creature read as having
 * already meditated, while a Sun Serpent that meditated joined the School pack and
 * took its wet-tile luck. The comment above `F_PREEMPTED` said to check by
 * listing rather than by reading a comment about ranges, and then took 19 and
 * 20 from exactly such a comment - `F_RETAL_MASK` is `3 << 19`, so the range
 * 17..23 was owned by this file and *not* free inside it.
 *
 * The fix is a second word rather than a fourth attempt at finding a gap. The
 * three that collided moved here as `F2_*`, and everything minted since starts
 * here too, so `flags` never has to be audited again.
 */

import {
  F_DEFENDING, F_MOVED, F_RETALIATED, F_WAITED, type StackStore,
} from "./state.js";

/** Set once a good-morale extra action has been granted this turn (the classic grants at most one). */
export const F_MORALE_USED = 1 << 17;
/** Set when the active stack's turn has been opened (defend cleared, bad morale rolled). */
export const F_TURN_STARTED = 1 << 18;
/** Two-bit counter of retaliations already spent this round: bits 19 and 20. */
export const F_RETAL_SHIFT = 19;
export const F_RETAL_MASK = 3 << F_RETAL_SHIFT;
/** Summoned by a spell or ability; does not count toward casualties. */
export const F_SUMMONED = 1 << 21;
/** Spent its action this round; distinct from F_MOVED, which only means "changed hex". */
export const F_ACTED = 1 << 22;
/** A RESURRECT_SELF stack has already used its one rebirth this battle. */
export const F_REBORN = 1 << 23;

/**
 * A phantom copy conjured by Clone. It fights, it is never folded back into an
 * army, and it goes the moment the spell that made it lapses - which is what
 * the modifier the spell left on it is for, and why this is a flag rather than
 * a countdown of its own.
 */
export const F_CLONE = 1 << 27;
/**
 * Fighting for the other side while Enthrall lasts. The stack's `side` column
 * is flipped rather than shadowed, so every rule that reads a side - targeting,
 * retaliation, the turn order - is right by construction; this bit is only how
 * the kernel remembers to flip it back.
 */
export const F_HYPNOTIZED = 1 << 28;

/** Cleared for every stack when a new round begins. */
export const ROUND_SCOPED_FLAGS =
  F_MOVED | F_WAITED | F_DEFENDING | F_RETALIATED | F_RETAL_MASK |
  F_MORALE_USED | F_TURN_STARTED | F_ACTED;

// ------------------------------------------------------------ the second word
//
// `StackStore.flags2`. Bit numbers here are independent of the first word's, so
// F2_PREEMPTED is bit 0 and shares nothing with F_ALIVE.

/**
 * Preemptive shot has been spent this round. Round-scoped rather than
 * turn-scoped because the expansion prices the Pistoleer's at one *per round* - it is a
 * reaction to being shot at, which can happen on someone else's turn.
 */
export const F2_PREEMPTED = 1 << 0;

/**
 * Sun Serpent meditation. `F2_MEDITATING` lasts until the stack's next turn opens;
 * `F2_MEDITATED` is the once-a-battle latch and is deliberately NOT round-scoped.
 */
export const F2_MEDITATING = 1 << 1;
export const F2_MEDITATED = 1 << 2;

/**
 * A gated duplicate: Emberpit's racial stands a second, weaker copy of a stack on
 * the field for the rest of the fight. It carries `F_SUMMONED` too, so nothing
 * folds it back into an army; this bit is what lets a rule say "this is a gate,
 * not a Summon Elemental" without naming the faction.
 *
 * Bit 3 rather than a later one because two bits minted beside it were removed
 * before they shipped: a `F2_DELAYED` for Hornet Cloud and a `F2_ACTIVATED_EVER`
 * for a used ability, both of which were written and neither of which anything
 * ever read. `waited[]` already says a stack is in the wait queue and
 * `cooldown[]` already says an ability is spent, so the two bits were a second
 * copy of a fact - and a flag nothing reads is one a later rule will read and
 * find stale. Bits 6 and up are free.
 */
export const F2_GATED = 1 << 3;

/**
 * Held in the branches: this stack may not walk (Barkwarden's Binding).
 *
 * It is a flag rather than a speed modifier, and the difference matters.
 * `effectiveSpeed` is both the reach and the INITIATIVE - a bound stack that
 * had its speed set to zero would also act last in the round, and the classic's binding
 * does not touch the order at all. A stack in the branches still gets its turn
 * on time, may still attack anything it can already reach, and still
 * retaliates; what it cannot do is leave.
 *
 * Set by `onDamageAbilities` when a binder connects, cleared in `beginTurn`
 * when no living adjacent binder is left. Storing "who bound me" would need a
 * column on `StackStore`, which is the save format; asking the board is a
 * question with the same answer and no migration.
 */
export const F2_BOUND = 1 << 4;

/**
 * Too frightened to strike: this stack may not attack or shoot on its next turn
 * (Fear). It may still walk, defend, wait and retaliate.
 *
 * Deliberately weaker than `F_BLINDED`, which takes the turn outright. Fear is
 * the shape H5 gives it - the unit is still there and still in the way, it
 * simply will not commit - and a second "lose your turn" flag would be a second
 * name for Blind.
 *
 * Round-scoped: it is set when a blow lands and gone at the next round
 * boundary, so it costs the target one turn's aggression rather than settling
 * in. That is what makes it a rider on an attack instead of a soft Blind.
 */
export const F2_AFRAID = 1 << 5;

/** Cleared for every stack when a new round begins. */
export const ROUND_SCOPED_FLAGS2 = F2_PREEMPTED | F2_AFRAID;

export const retaliationsUsed = (s: StackStore, i: number): number =>
  (s.flags[i]! & F_RETAL_MASK) >> F_RETAL_SHIFT;
